/**
 * AI Scribe V2 — draft/export actions
 *
 * Owns the note-generation and add-to-visit wiring so the window component stays
 * presentational. Finishing drafts a note from the captured transcript; adding
 * to visit exports the selected sections and then closes the session.
 */

import { useQueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useCallback } from "react";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useAiScriberExportNote } from "@/queries/ai-scriber/use-ai-scriber-export-note";
import { useAiScriberAudioNoteGenerate } from "@/queries/ai-scriber/use-scriber-audio-note-generate";
import {
  ClinicalNotesDTO,
  ClinicalNotesSaveRequestNoteTypeEnum,
  NoteGenerationRequestNoteTypeEnum,
} from "@/services";
import { showToastOnApiCall } from "@/utils/utils";
import { VisitNote } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/ActiveForm";
import { ScribeStatus, ScribeTranscriptLine } from "./types";
import { toTranscriptionSegments } from "./utils";

interface UseScribeNoteActionsParams {
  transcript: ScribeTranscriptLine[];
  noteType: VisitNote;
  patientUuid?: string;
  appointmentId?: string | null;
  selectedSectionData: ClinicalNotesDTO | null;
  /** Trimmed "Instructions to Scribe" text; blank is left off the request. */
  instructions: string;
  stop: () => void;
  setStatus: Dispatch<SetStateAction<ScribeStatus>>;
  setDraftSegments: Dispatch<
    SetStateAction<ScribeTranscriptLine[] | undefined>
  >;
  /** Closes the window once the note has been exported. */
  onCloseWindow: () => void;
}

export interface ScribeNoteActions {
  handleClose: () => void;
  handleFinish: () => void;
  handleAddToVisit: () => void;
}

export const useScribeNoteActions = ({
  transcript,
  noteType,
  patientUuid,
  appointmentId,
  selectedSectionData,
  instructions,
  stop,
  setStatus,
  setDraftSegments,
  onCloseWindow,
}: UseScribeNoteActionsParams): ScribeNoteActions => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: generateNote } = useAiScriberAudioNoteGenerate();
  const { mutateAsync: exportNote } = useAiScriberExportNote();

  const handleClose = useCallback(() => {
    stop();
    onCloseWindow();
  }, [stop, onCloseWindow]);

  const handleFinish = useCallback(() => {
    stop();
    setStatus("FINALIZING");
    setDraftSegments(transcript);

    showToastOnApiCall({
      showSnackbar,
      apiCall: generateNote({
        segments: toTranscriptionSegments(transcript),
        /** The generated enum lags the template list — send what was picked. */
        noteType: noteType as NoteGenerationRequestNoteTypeEnum,
        patientId: patientUuid,
        appointmentId: appointmentId ?? undefined,
        instructions: instructions || undefined,
      }),
    });
  }, [
    stop,
    setStatus,
    setDraftSegments,
    transcript,
    showSnackbar,
    generateNote,
    noteType,
    patientUuid,
    appointmentId,
    instructions,
  ]);

  const handleAddToVisit = useCallback(() => {
    showToastOnApiCall({
      apiCall: exportNote({
        clinicalNotesSaveRequest: {
          data: selectedSectionData ?? undefined,
          patientId: patientUuid,
          appointmentId: appointmentId ?? undefined,
          /** The header picker, not the template the encounter was opened with. */
          noteType: noteType as ClinicalNotesSaveRequestNoteTypeEnum,
        },
      }),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({ queryKey: ["encounter-summery-v2"] });
        handleClose();
      },
    });
  }, [
    exportNote,
    selectedSectionData,
    patientUuid,
    appointmentId,
    noteType,
    showSnackbar,
    queryClient,
    handleClose,
  ]);

  return { handleClose, handleFinish, handleAddToVisit };
};
